import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CurrentStateService } from '@ddv/behaviors';
import {
    ConfirmationPopupService,
    CustomFormValidators,
    ModalDialogActive,
    MultiSubscriptionComponent,
} from '@ddv/common-components';
import { FeatureFlagService, UserData, UserEntitlements } from '@ddv/entitlements';
import { getCrosstalkManagementLocation, getNQSHostForManageNamedQueriesLink } from '@ddv/http';
import { ManagerService } from '@ddv/layout';
import {
    AppWidgetState,
    getGlobalSaveDialogOptions,
    NamedQuery,
    Sharing,
    UserPreferences,
    WidgetTypes,
} from '@ddv/models';
import { clone } from '@ddv/utils';
import { switchMap } from 'rxjs';

import { LegacyWidgetsService } from '../../services/legacy-widgets.service';
import { WidgetsService } from '../../services/widgets.service';

// This component is only dynamically instantiated and opened as a modal from WidgetHeaderComponent and ManageWidgetsDashboardComponent
// therefore you will not find any references to its selector
@Component({
    selector: 'app-save-widget-form',
    templateUrl: 'save-widget-form.component.html',
    styleUrls: ['./save-widget-form.component.scss'],
    standalone: false,
})
export class SaveWidgetFormComponent extends MultiSubscriptionComponent implements OnInit {
    @Input() userPreferences: UserPreferences | undefined;
    @Input() formData: FormData | undefined;
    @Input() isCopy = false;
    @Input() isDashboardWidget = false;
    @Input() coreWidgetType: WidgetTypes | undefined;
    @Input() namedQuery: NamedQuery | undefined;
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;
    @Input() clientCode = '';
    @Input() sharing: Sharing = { toUsers: [], toTeams: [] };

    @Output() widgetSaveFormSubmit = new EventEmitter<AppWidgetState>();

    protected modalDialogActive = inject(ModalDialogActive<void>);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly featureFlagsService = inject(FeatureFlagService);
    private readonly legacyWidgetsService = inject(LegacyWidgetsService);
    private readonly managerService = inject(ManagerService);
    private readonly widgetsService = inject(WidgetsService);

    isSaveInvoked = false;
    isReadOnly = false;
    isGlobal = false;
    saveWidgetForm: UntypedFormGroup | undefined;
    isStackedQuery = false;
    disableEditGlobalWidget = false;

    protected isHSUser = false;
    protected isMultiClient = false;
    protected queryName: string | undefined;
    protected queryId: string | undefined;
    protected queryTypeName: string | undefined;
    protected crosstalkSchema: string | undefined;
    protected queryNameLink: string | undefined;
    protected crosstalkSchemaLink: string | undefined;

    private isGlobalEditPartial = false;

    ngOnInit(): void {
        this.initForm(clone({
            id: null,
            name: '',
            displayNameType: 'CONFIGURED',
            customDisplayName: '',
            enableWidgetHeader: true,
            description: '',
            isSubscribedToDashboardFilters: true,
            isShared: false,
            isSelectable: true,
        }, this.formData));

        if (this.formData) {
            this.isGlobal = !!this.formData.isGlobal;

            if (this.formData?.id) {
                this.markFormGroupTouched();
            } else if (this.formData?.name) {
                this.saveWidgetForm?.get('name')?.markAsTouched();
            }

            this.validateDuplicate();
        }

        this.isGlobalEditPartial = !!this.entitlements?.hasGlobalEditPartial();
        this.disableEditGlobalWidget = this.isGlobalEditPartial && this.isGlobal;

        this.isHSUser = !!this.user?.internal;
        if (this.isHSUser && this.isDashboardWidget) {
            this.setQueryBasedProperties(this.managerService.getWidgetPreferences(this.formData?.id ?? 0));
        }

        this.subscribeTo(this.currentStateService.isMultiClient$, (isMultiClient: boolean) => {
            this.isMultiClient = isMultiClient;
        });
    }

    saveWidgetInfo(data: FormData): void {
        if (this.isGlobal && !this.isGlobalEditPartial) {
            const confirmDialogOptions = getGlobalSaveDialogOptions();
            this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
                next: (action) => {
                    if (action === 'confirm') {
                        this.onWidgetInfoSave(data);
                    }
                },
            });
        } else {
            this.onWidgetInfoSave(data);
        }
    }

    onWidgetInfoSave(data: FormData): void {
        if (!data.id || data.oldName !== data.name) {
            this.featureFlagsService.isFlagEnabled('use-aws-for-widgets', this.clientCode)
                .pipe(
                    switchMap((isFlagEnabled) => {
                        if (isFlagEnabled) {
                            return this.widgetsService.validateWidgetName(data.name ?? '', this.clientCode);
                        }
                        return this.legacyWidgetsService.validateWidgetName(data.name ?? '');
                    }))
                .subscribe((result) => {
                    if (result) {
                        this.invokeWidgetSave(result, data);
                    } else {
                        this.saveWidgetForm?.get('name')?.setErrors({ isDuplicate: true });
                    }
                });
        } else {
            this.invokeWidgetSave(true, data);
        }
    }

    validateDuplicate(): void {
        if (!this.saveWidgetForm) {
            return;
        }

        const formNameCtrl = this.saveWidgetForm.get('name');
        const formGroupValue = this.saveWidgetForm.getRawValue() as FormData;
        if (formNameCtrl?.valid && formGroupValue.name && (!formGroupValue.id || formGroupValue.oldName !== formGroupValue.name)) {
            this.featureFlagsService.isFlagEnabled('use-aws-for-widgets', this.clientCode)
                .pipe(
                    switchMap((isFlagEnabled) => {
                        if (isFlagEnabled) {
                            return this.widgetsService.validateWidgetName(formGroupValue.name ?? '', this.clientCode);
                        }
                        return this.legacyWidgetsService.validateWidgetName(formGroupValue.name ?? '');
                    }))
                .subscribe((result) => {
                    if (result) {
                        formNameCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                    } else {
                        formNameCtrl.setErrors({ isDuplicate: true });
                    }
                });
        }
    }

    isSaveDisabled(): boolean {
        const userPermission = this.sharing.toUsers
            .some((user) => user.permission === 'write' && user.name.toLocaleLowerCase() === this.userPreferences?.username.toLocaleLowerCase());

        return !this.saveWidgetForm?.valid ||
            this.isSaveInvoked ||
            (this.formData?.privilege === 'VIEW' && !this.isDashboardWidget && !this.isCopy && !userPermission);
    }

    isAutoOptionDisabled(): boolean | null {
        return (this.isReadOnly || this.coreWidgetType === 'SUMMARY') ? true : null;
    }

    private invokeWidgetSave(validName: boolean, data: FormData): void {
        if (validName) {
            data.sharing = data.isShared ? this.sharing : { toUsers: [], toTeams: [] };
            this.isSaveInvoked = true;
            this.widgetSaveFormSubmit.emit(data);
        } else {
            this.saveWidgetForm?.get('name')?.setErrors({ isDuplicate: true });
        }
    }

    private initForm(data: AppWidgetState): void {
        this.saveWidgetForm = this.fb.group({
            id: [data.id],
            oldName: [data.name],
            name: [data.name, Validators.compose([
                Validators.required,
                CustomFormValidators.isInputSpaces,
                Validators.pattern(/^[\w\s/\-%&^#@()<>]+$/),
                Validators.maxLength(40)],
            )],
            displayNameType: [data.displayNameType],
            customDisplayName: [data.customDisplayName, Validators.compose([
                Validators.pattern(/^[\w\s/\-%&^#@()<>]+$/),
                Validators.maxLength(40)],
            )],
            enableWidgetHeader: [data.enableWidgetHeader],
            isSelectable: [data.isSelectable],
            description: [data.description],
            isSubscribedToDashboardFilters: [data.isSubscribedToDashboardFilters],
            isShared: [data.isShared],
            version: [data.version],
        }, {
            validator: (group: { controls: { displayNameType: { value: string }, customDisplayName: AbstractControl }}) => {
                if (group.controls.displayNameType.value === 'CUSTOM') {
                    return Validators.required(group.controls.customDisplayName);
                }
                group.controls.customDisplayName.reset(null, { onlySelf: true, emitEvent: false });
                return null;
            },
        });
    }

    // Marks all controls in a form group as touched
    private markFormGroupTouched(formGroup = this.saveWidgetForm): void {
        if (!formGroup) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Object.keys(formGroup.controls).map((key) => formGroup.controls[key]).forEach((control: any) => {
            control.markAsTouched();
            if (control.controls) {
                control.controls.forEach((c: UntypedFormGroup) => this.markFormGroupTouched(c));
            }
        });
    }

    private setQueryBasedProperties(widgetPreferences: AppWidgetState | undefined | null): void {
        if (!widgetPreferences) {
            return;
        }

        this.queryName = this.namedQuery?.name ?? '';
        this.queryId = this.namedQuery?.id ?? '';
        this.queryTypeName = this.namedQuery?.normalizeQueryTypeName() ?? '';

        if (this.queryId && this.clientCode) {
            this.queryNameLink = `https://${getNQSHostForManageNamedQueriesLink()}/#/${this.clientCode}/queries/${this.queryId}`;
        }

        if (widgetPreferences.currentVisualization === 'ADVANCED_GRID') {
            this.crosstalkSchema = this.namedQuery?.normalizeConversableType() ?? '';
            if (this.crosstalkSchema && this.clientCode) {
                this.crosstalkSchemaLink = `${getCrosstalkManagementLocation()}/#/${this.clientCode}/management/comment-import/${this.crosstalkSchema}`;
            }
        }
    }
}

interface FormData extends AppWidgetState {
    oldName?: string;
}
